import React, { useState, useEffect } from 'react';
import {
    Table,
    Card,
    Tag,
    Select,
    Input,
    Space,
    Badge,
    Statistic,
    Row,
    Col,
    Alert,
    Tooltip,
    Typography,
    Tabs,
    Divider,
    Button,
    Modal,
    Form,
    DatePicker,
    message,
    Dropdown,
    Menu
} from 'antd';
import {
    WarningOutlined,
    ClockCircleOutlined,
    InfoCircleOutlined,
    SearchOutlined,
    UserOutlined,
    CalendarOutlined,
    BookOutlined,
    TeamOutlined,
    SwapOutlined,
    DownloadOutlined,
    FileExcelOutlined,
    MoreOutlined,
    PlusOutlined
} from '@ant-design/icons';
import { HijosService } from 'services/derecho-habiente/HijosService';
import ColaboradorService from 'services/ColaboradorService';
import moment from 'moment';
import 'moment/locale/es';

const { Option } = Select;
const { Text, Title } = Typography;
const { TabPane } = Tabs;

moment.locale('es');

const BandejaDerechoHabienteHijos = () => {
    const [datos, setDatos] = useState([]);
    const [datosFiltrados, setDatosFiltrados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [estadisticas, setEstadisticas] = useState({});
    const [tabActiva, setTabActiva] = useState('TODOS');
    const [filtros, setFiltros] = useState({
        nivelAlerta: null,
        busqueda: '',
        mesesAnticipacion: 12,
        tipo: null
    });
    
    // Modal para agregar hijo
    const [modalAgregarVisible, setModalAgregarVisible] = useState(false);
    const [formAgregar] = Form.useForm();
    const [loadingAgregar, setLoadingAgregar] = useState(false);
    const [tipoHijoSeleccionado, setTipoHijoSeleccionado] = useState(null);
    const [colaboradoresBusqueda, setColaboradoresBusqueda] = useState([]);

    // Modal para mover a estudios superiores
    const [modalVisible, setModalVisible] = useState(false);
    const [hijoSeleccionado, setHijoSeleccionado] = useState(null);
    const [formMover] = Form.useForm();
    const [loadingMover, setLoadingMover] = useState(false);
    
    // Modal para reporte de asignación familiar
    const [modalReporteVisible, setModalReporteVisible] = useState(false);
    const [datosReporte, setDatosReporte] = useState([]);
    const [loadingReporte, setLoadingReporte] = useState(false);

    useEffect(() => {
        cargarDatos();
        cargarEstadisticas();
    }, [filtros.mesesAnticipacion, filtros.tipo]);

    useEffect(() => {
        aplicarFiltros();
    }, [datos, filtros.nivelAlerta, filtros.busqueda, tabActiva]);

    const cargarDatos = async () => {
    setLoading(true);
    try {
        let resultado;
        
        if (tabActiva === 'TODOS') {
            // Tab TODOS: cargar todos los hijos sin filtro de proximidad
            resultado = await HijosService.obtenerTodosLosHijos();
        } else {
            // Otras tabs: usar el endpoint con filtros
            const params = {
                mesesAnticipacion: filtros.mesesAnticipacion,
                tipo: tabActiva !== 'TODOS' ? tabActiva : null
            };
            resultado = await HijosService.obtenerHijosProximosLimite(params);
        }
        
        setDatos(resultado);
    } catch (error) {
        console.error('Error al cargar datos:', error);
        message.error('Error al cargar los datos');
    } finally {
        setLoading(false);
    }
};

// También actualizar el useEffect para recargar cuando cambia el tab
useEffect(() => {
    cargarDatos();
}, [tabActiva, filtros.mesesAnticipacion]);

    const cargarEstadisticas = async () => {
        try {
            const resultado = await HijosService.obtenerEstadisticas({});
            setEstadisticas(resultado);
        } catch (error) {
            console.error('Error al cargar estadísticas:', error);
        }
    };

    const aplicarFiltros = () => {
        let resultado = [...datos];

        // Filtro por tab (tipo)
        if (tabActiva === 'MENOR_EDAD') {
            resultado = resultado.filter(item => item.tipoDerechoHabiente === 'MENOR_EDAD');
        } else if (tabActiva === 'ESTUDIO_SUPERIOR') {
            resultado = resultado.filter(item => item.tipoDerechoHabiente === 'ESTUDIO_SUPERIOR');
        }

        // Filtro por nivel de alerta
        if (filtros.nivelAlerta) {
            resultado = resultado.filter(item => item.nivelAlerta === filtros.nivelAlerta);
        }

        // Filtro por búsqueda
        if (filtros.busqueda) {
            const busqueda = filtros.busqueda.toLowerCase();
            resultado = resultado.filter(item =>
                item.colaboradorNombre?.toLowerCase().includes(busqueda) ||
                item.hijoNombre?.toLowerCase().includes(busqueda) ||
                item.colaboradorDocumento?.includes(busqueda) ||
                item.hijoDocumento?.includes(busqueda) ||
                item.departamento?.toLowerCase().includes(busqueda) ||
                item.cargo?.toLowerCase().includes(busqueda) ||
                item.centroEstudios?.toLowerCase().includes(busqueda) ||
                item.carrera?.toLowerCase().includes(busqueda)
            );
        }

        setDatosFiltrados(resultado);
    };

    const obtenerColorAlerta = (nivelAlerta) => {
        switch (nivelAlerta) {
            case 'CRITICO':
                return { color: 'red', icon: <WarningOutlined /> };
            case 'ADVERTENCIA':
                return { color: 'orange', icon: <ClockCircleOutlined /> };
            case 'INFO':
                return { color: 'blue', icon: <InfoCircleOutlined /> };
            default:
                return { color: 'default', icon: null };
        }
    };

    const obtenerTextoAlerta = (nivelAlerta) => {
        switch (nivelAlerta) {
            case 'CRITICO':
                return 'Urgente (0-3 meses)';
            case 'ADVERTENCIA':
                return 'Próximo (3-6 meses)';
            case 'INFO':
                return 'Informativo (6-12 meses)';
            default:
                return nivelAlerta;
        }
    };

    const obtenerIconoTipo = (tipo) => {
        return tipo === 'ESTUDIO_SUPERIOR' ? <BookOutlined /> : <UserOutlined />;
    };

    const obtenerColorTipo = (tipo) => {
        return tipo === 'ESTUDIO_SUPERIOR' ? 'purple' : 'cyan';
    };

    // Buscar colaboradores para el selector del modal agregar
    const onBuscarColaboradores = async (value) => {
        if (!value || value.trim().length < 2) {
            setColaboradoresBusqueda([]);
            return;
        }
        try {
            const respuesta = await ColaboradorService.listarPorNombreCompletoODni({ filtro: value.trim() });
            setColaboradoresBusqueda(respuesta.colaboradores ?? []);
        } catch {
            setColaboradoresBusqueda([]);
        }
    };

    // Abrir modal agregar hijo
    const abrirModalAgregarHijo = () => {
        formAgregar.resetFields();
        setTipoHijoSeleccionado(null);
        setColaboradoresBusqueda([]);
        setModalAgregarVisible(true);
    };

    // Guardar nuevo hijo
    const handleAgregarHijo = async (values) => {
        setLoadingAgregar(true);
        try {
            await HijosService.agregarHijo({
                colaboradorId: values.colaboradorId,
                tipo: values.tipo,
                nombreCompleto: values.nombreCompleto,
                numeroDocumentoIdentidad: values.numeroDocumentoIdentidad,
                fechaNacimiento: values.fechaNacimiento.toISOString(),
                centroEstudios: values.centroEstudios,
                carrera: values.carrera,
                cicloSemestre: values.cicloSemestre,
            });
            message.success('Hijo agregado exitosamente');
            setModalAgregarVisible(false);
            cargarDatos();
            cargarEstadisticas();
        } catch (error) {
            message.error(error?.response?.data?.mensaje ?? 'Error al agregar el hijo');
        } finally {
            setLoadingAgregar(false);
        }
    };

    // Función para abrir modal de mover a estudios superiores
    const abrirModalMoverEstudio = (record) => {
        setHijoSeleccionado(record);
        formMover.resetFields();
        setModalVisible(true);
    };

    // Función para mover hijo a estudios superiores
    const handleMoverEstudioSuperior = async (values) => {
        setLoadingMover(true);
        try {
            await HijosService.moverHijoAEstudioSuperior({
                colaboradorId: hijoSeleccionado.colaboradorId,
                hijoNumeroDocumentoIdentidad: hijoSeleccionado.hijoDocumento,
                centroEstudios: values.centroEstudios,
                carrera: values.carrera,
                cicloSemestre: values.cicloSemestre
            });
            
            message.success('Hijo movido a Estudios Superiores exitosamente');
            setModalVisible(false);
            cargarDatos();
            cargarEstadisticas();
        } catch (error) {
            console.error('Error al mover hijo:', error);
            message.error('Error al mover el hijo a Estudios Superiores');
        } finally {
            setLoadingMover(false);
        }
    };

    // Función para cargar reporte de asignación familiar
    const cargarReporteAsignacionFamiliar = async () => {
        setLoadingReporte(true);
        try {
            const resultado = await HijosService.obtenerReporteAsignacionFamiliar();
            setDatosReporte(resultado);
            setModalReporteVisible(true);
        } catch (error) {
            console.error('Error al cargar reporte:', error);
            message.error('Error al cargar el reporte de asignación familiar');
        } finally {
            setLoadingReporte(false);
        }
    };

    // Función para descargar Excel
    const descargarExcel = async () => {
        setLoadingReporte(true);
        try {
            const blob = await HijosService.descargarExcelAsignacionFamiliar();
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `reporte_asignacion_familiar_${moment().format('YYYYMMDD')}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            message.success('Excel descargado exitosamente');
        } catch (error) {
            console.error('Error al descargar Excel:', error);
            message.error('Error al descargar el Excel');
        } finally {
            setLoadingReporte(false);
        }
    };

    // Menú de acciones
    const getMenuAcciones = (record) => (
        <Menu>
            {record.tipoDerechoHabiente === 'MENOR_EDAD' && (
                <Menu.Item 
                    key="mover" 
                    icon={<SwapOutlined />}
                    onClick={() => abrirModalMoverEstudio(record)}
                >
                    Mover a Estudios Superiores
                </Menu.Item>
            )}
        </Menu>
    );

    const columns = [
        {
            title: 'Alerta',
            key: 'alerta',
            width: 80,
            fixed: 'left',
            align: 'center',
            filters: [
                { text: 'Urgente (0-3 meses)', value: 'CRITICO' },
                { text: 'Próximo (3-6 meses)', value: 'ADVERTENCIA' },
                { text: 'Informativo (6-12 meses)', value: 'INFO' },
            ],
            onFilter: (value, record) => record.nivelAlerta === value,
            render: (_, record) => {
                const alerta = obtenerColorAlerta(record.nivelAlerta);
                return (
                    <Tooltip title={obtenerTextoAlerta(record.nivelAlerta)}>
                        <Badge
                            count={alerta.icon}
                            style={{
                                backgroundColor: alerta.color === 'red' ? '#ff4d4f' :
                                    alerta.color === 'orange' ? '#faad14' : '#1890ff'
                            }}
                        />
                    </Tooltip>
                );
            },
        },
        {
            title: 'Tipo',
            key: 'tipo',
            width: 160,
            filters: [
                { text: 'Menor de Edad', value: 'MENOR_EDAD' },
                { text: 'Estudio Superior', value: 'ESTUDIO_SUPERIOR' },
            ],
            onFilter: (value, record) => record.tipoDerechoHabiente === value,
            render: (_, record) => (
                <Tag 
                    color={obtenerColorTipo(record.tipoDerechoHabiente)} 
                    icon={obtenerIconoTipo(record.tipoDerechoHabiente)}
                >
                    {record.tipoDerechoHabiente === 'ESTUDIO_SUPERIOR' ? 'Est. Superior' : 'Menor Edad'}
                </Tag>
            ),
        },
        {
            title: 'Colaborador',
            key: 'colaborador',
            width: 200,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{record.colaboradorNombre}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        DNI: {record.colaboradorDocumento}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Hijo(a)',
            key: 'hijo',
            width: 200,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text>{record.hijoNombre}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        DNI: {record.hijoDocumento}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Centro de Estudios',
            key: 'estudios',
            width: 200,
            render: (_, record) => {
                if (record.tipoDerechoHabiente !== 'ESTUDIO_SUPERIOR') {
                    return <Text type="secondary">-</Text>;
                }
                return (
                    <Space direction="vertical" size={0}>
                        <Text>{record.centroEstudios || '-'}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {record.carrera || ''} {record.cicloSemestre ? `(${record.cicloSemestre})` : ''}
                        </Text>
                    </Space>
                );
            },
        },
        {
            title: 'Fecha Nacimiento',
            dataIndex: 'hijoFechaNacimiento',
            key: 'fechaNacimiento',
            width: 140,
            render: (fecha) => (
                <Space>
                    <CalendarOutlined />
                    {moment(fecha).format('DD/MM/YYYY')}
                </Space>
            ),
        },
        {
            title: 'Edad Actual',
            dataIndex: 'edadActual',
            key: 'edadActual',
            width: 100,
            align: 'center',
            render: (edad) => (
                <Tag color="geekblue">{edad} años</Tag>
            ),
        },
        {
            title: 'Tiempo Restante',
            key: 'tiempoRestante',
            width: 150,
            align: 'center',
            sorter: (a, b) => a.mesesParaLimite - b.mesesParaLimite,
            render: (_, record) => {
                const alerta = obtenerColorAlerta(record.nivelAlerta);
                return (
                    <Tooltip title={`Cumple ${record.edadLimite} años`}>
                        <Tag color={alerta.color} icon={alerta.icon}>
                            {record.mesesParaLimite} {record.mesesParaLimite === 1 ? 'mes' : 'meses'}
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Fecha Límite',
            key: 'fechaLimite',
            width: 140,
            render: (_, record) => (
                <Tooltip title={`Fecha en que cumple ${record.edadLimite} años`}>
                    <Text strong style={{ color: '#1890ff' }}>
                        {moment(record.fechaLimite).format('DD/MM/YYYY')}
                    </Text>
                </Tooltip>
            ),
        },
        {
            title: 'Acciones',
            key: 'acciones',
            width: 100,
            fixed: 'right',
            align: 'center',
            render: (_, record) => {
                if (record.tipoDerechoHabiente === 'MENOR_EDAD') {
                    return (
                        <Dropdown overlay={getMenuAcciones(record)} trigger={['click']}>
                            <Button type="text" icon={<MoreOutlined />} />
                        </Dropdown>
                    );
                }
                return null;
            },
        }
    ];

    // Columnas del reporte de asignación familiar
    const columnsReporte = [
        {
            title: 'Colaborador',
            key: 'colaborador',
            width: 200,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{record.colaboradorNombre}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        DNI: {record.colaboradorDocumento}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Hijo(a)',
            key: 'hijo',
            width: 180,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text>{record.hijoNombre}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        {record.edadActual} años
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Tipo',
            dataIndex: 'tipo',
            key: 'tipo',
            width: 130,
            render: (tipo) => (
                <Tag color={tipo === 'MENOR_EDAD' ? 'cyan' : 'purple'}>
                    {tipo === 'MENOR_EDAD' ? 'Menor Edad' : 'Est. Superior'}
                </Tag>
            ),
        },
        {
            title: 'Aplica',
            key: 'aplica',
            width: 80,
            align: 'center',
            render: (_, record) => (
                <Tag color={record.aplicaAsignacionFamiliar ? 'green' : 'red'}>
                    {record.aplicaAsignacionFamiliar ? 'SÍ' : 'NO'}
                </Tag>
            ),
        },
        {
            title: 'Motivo',
            dataIndex: 'motivoAplicacion',
            key: 'motivo',
            width: 200,
        },
    ];

    const renderEstadisticaCard = (titulo, stats, color, icon) => (
        <Card size="small">
            <Row gutter={8}>
                <Col span={24}>
                    <Statistic
                        title={titulo}
                        value={stats?.total || 0}
                        prefix={icon}
                        valueStyle={{ fontSize: 20 }}
                    />
                </Col>
            </Row>
            <Divider style={{ margin: '8px 0' }} />
            <Row gutter={8}>
                <Col span={8}>
                    <Text type="danger" style={{ fontSize: 12 }}>
                        <WarningOutlined /> {stats?.criticos || 0}
                    </Text>
                </Col>
                <Col span={8}>
                    <Text style={{ color: '#faad14', fontSize: 12 }}>
                        <ClockCircleOutlined /> {stats?.advertencias || 0}
                    </Text>
                </Col>
                <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <InfoCircleOutlined /> {stats?.informativos || 0}
                    </Text>
                </Col>
            </Row>
        </Card>
    );

    return (
        <div style={{ padding: 24 }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Title level={3}>
                        <TeamOutlined /> Gestión de Derecho Habientes - Hijos
                    </Title>
                </Col>
                <Col>
                    <Space>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={abrirModalAgregarHijo}
                        >
                            Agregar Hijo
                        </Button>
                        <Button
                            type="primary"
                            icon={<FileExcelOutlined />}
                            onClick={descargarExcel}
                            loading={loadingReporte}
                        >
                            Reporte Asignación Familiar
                        </Button>
                    </Space>
                </Col>
            </Row>

            {/* Estadísticas */}
            {/* <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8}>
                    {renderEstadisticaCard(
                        'Total General',
                        {
                            total: estadisticas.totalGeneral,
                            criticos: estadisticas.criticosGeneral,
                            advertencias: estadisticas.advertenciasGeneral,
                            informativos: estadisticas.informativosGeneral
                        },
                        'default',
                        <TeamOutlined />
                    )}
                </Col>
                <Col xs={24} sm={8}>
                    {renderEstadisticaCard(
                        'Menores de Edad (18 años)',
                        {
                            total: estadisticas.totalMenoresEdad,
                            criticos: estadisticas.criticosMenoresEdad,
                            advertencias: estadisticas.advertenciasMenoresEdad,
                            informativos: estadisticas.informativosMenoresEdad
                        },
                        'cyan',
                        <UserOutlined />
                    )}
                </Col>
                <Col xs={24} sm={8}>
                    {renderEstadisticaCard(
                        'Estudios Superiores (24 años)',
                        {
                            total: estadisticas.totalEstudioSuperior,
                            criticos: estadisticas.criticosEstudioSuperior,
                            advertencias: estadisticas.advertenciasEstudioSuperior,
                            informativos: estadisticas.informativosEstudioSuperior
                        },
                        'purple',
                        <BookOutlined />
                    )}
                </Col>
            </Row> */}

            {/* Alertas urgentes */}
            {(estadisticas.criticosGeneral > 0) && (
                <Alert
                    message="Atención Urgente Requerida"
                    description={
                        <Space direction="vertical">
                            {estadisticas.criticosMenoresEdad > 0 && (
                                <Text>
                                    <UserOutlined /> {estadisticas.criticosMenoresEdad} hijo(s) menor(es) cumplirán 18 años en los próximos 3 meses.
                                </Text>
                            )}
                            {estadisticas.criticosEstudioSuperior > 0 && (
                                <Text>
                                    <BookOutlined /> {estadisticas.criticosEstudioSuperior} hijo(s) con estudios superiores cumplirán 24 años en los próximos 3 meses.
                                </Text>
                            )}
                        </Space>
                    }
                    type="error"
                    showIcon
                    icon={<WarningOutlined />}
                    style={{ marginBottom: 16 }}
                    closable
                />
            )}

            {/* Tabs y Filtros */}
            <Card style={{ marginBottom: 16 }}>
                <Tabs 
                    activeKey={tabActiva} 
                    onChange={setTabActiva}
                    style={{ marginBottom: 16 }}
                >
                    <TabPane 
                        tab={<span><TeamOutlined /> Todos ({estadisticas.totalGeneral || 0})</span>} 
                        key="TODOS" 
                    />
                    <TabPane 
                        tab={<span><UserOutlined /> Menores de Edad ({estadisticas.totalMenoresEdad || 0})</span>} 
                        key="MENOR_EDAD" 
                    />
                    <TabPane 
                        tab={<span><BookOutlined /> Estudios Superiores ({estadisticas.totalEstudioSuperior || 0})</span>} 
                        key="ESTUDIO_SUPERIOR" 
                    />
                </Tabs>

                <Space size="middle" wrap style={{ width: '100%' }}>
                    <Input
                        placeholder="Buscar por nombre, DNI, centro de estudios..."
                        prefix={<SearchOutlined />}
                        style={{ width: 350 }}
                        value={filtros.busqueda}
                        onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
                        allowClear
                    />

                    <Select
                        placeholder="Filtrar por nivel de alerta"
                        style={{ width: 220 }}
                        value={filtros.nivelAlerta}
                        onChange={(value) => setFiltros({ ...filtros, nivelAlerta: value })}
                        allowClear
                    >
                        <Option value="CRITICO">
                            <Tag color="red" icon={<WarningOutlined />}>Urgente (0-3 meses)</Tag>
                        </Option>
                        <Option value="ADVERTENCIA">
                            <Tag color="orange" icon={<ClockCircleOutlined />}>Próximo (3-6 meses)</Tag>
                        </Option>
                        <Option value="INFO">
                            <Tag color="blue" icon={<InfoCircleOutlined />}>Informativo (6-12 meses)</Tag>
                        </Option>
                    </Select>

                    <Select
                        placeholder="Anticipación"
                        style={{ width: 180 }}
                        value={filtros.mesesAnticipacion}
                        onChange={(value) => setFiltros({ ...filtros, mesesAnticipacion: value })}
                    >
                        <Option value={1}>1 mes</Option>
                        <Option value={3}>3 meses</Option>
                        <Option value={6}>6 meses</Option>
                        <Option value={12}>12 meses</Option>
                        <Option value={18}>18 meses</Option>
                        <Option value={24}>24 meses</Option>
                    </Select>
                </Space>
            </Card>

            {/* Tabla */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={datosFiltrados}
                    loading={loading}
                    rowKey={(record) => `${record.colaboradorId}-${record.hijoDocumento}-${record.tipoDerechoHabiente}`}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `Total: ${total} registro(s)`,
                    }}
                    scroll={{ x: 1700 }}
                    rowClassName={(record) => {
                        if (record.nivelAlerta === 'CRITICO') return 'row-critico';
                        if (record.nivelAlerta === 'ADVERTENCIA') return 'row-advertencia';
                        return '';
                    }}
                />
            </Card>

            {/* Modal para mover a estudios superiores */}
            <Modal
                title={
                    <Space>
                        <SwapOutlined />
                        <span>Mover a Estudios Superiores</span>
                    </Space>
                }
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={500}
            >
                {hijoSeleccionado && (
                    <>
                        <Alert
                            message="Información del Hijo"
                            description={
                                <Space direction="vertical" size={0}>
                                    <Text><strong>Nombre:</strong> {hijoSeleccionado.hijoNombre}</Text>
                                    <Text><strong>DNI:</strong> {hijoSeleccionado.hijoDocumento}</Text>
                                    <Text><strong>Edad:</strong> {hijoSeleccionado.edadActual} años</Text>
                                    <Text><strong>Colaborador:</strong> {hijoSeleccionado.colaboradorNombre}</Text>
                                </Space>
                            }
                            type="info"
                            style={{ marginBottom: 16 }}
                        />
                        
                        <Form
                            form={formMover}
                            layout="vertical"
                            onFinish={handleMoverEstudioSuperior}
                        >
                            <Form.Item
                                name="centroEstudios"
                                label="Centro de Estudios"
                                rules={[{ required: true, message: 'Ingrese el centro de estudios' }]}
                            >
                                <Input placeholder="Ej: Universidad Nacional Mayor de San Marcos" />
                            </Form.Item>
                            
                            <Form.Item
                                name="carrera"
                                label="Carrera"
                                rules={[{ required: true, message: 'Ingrese la carrera' }]}
                            >
                                <Input placeholder="Ej: Ingeniería de Sistemas" />
                            </Form.Item>
                            
                            <Form.Item
                                name="cicloSemestre"
                                label="Ciclo/Semestre"
                            >
                                <Input placeholder="Ej: 5to ciclo" />
                            </Form.Item>
                            
                            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                                <Space>
                                    <Button onClick={() => setModalVisible(false)}>
                                        Cancelar
                                    </Button>
                                    <Button 
                                        type="primary" 
                                        htmlType="submit"
                                        loading={loadingMover}
                                        icon={<SwapOutlined />}
                                    >
                                        Mover a Estudios Superiores
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </>
                )}
            </Modal>

            {/* Modal de reporte de asignación familiar */}
            <Modal
                title={
                    <Space>
                        <FileExcelOutlined />
                        <span>Reporte de Asignación Familiar</span>
                    </Space>
                }
                visible={modalReporteVisible}
                onCancel={() => setModalReporteVisible(false)}
                width={900}
                footer={[
                    <Button key="cancel" onClick={() => setModalReporteVisible(false)}>
                        Cerrar
                    </Button>,
                    <Button 
                        key="download" 
                        type="primary" 
                        icon={<DownloadOutlined />}
                        onClick={descargarExcel}
                    >
                        Descargar Excel
                    </Button>
                ]}
            >
                <Alert
                    message="Hijos que aplican para Asignación Familiar"
                    description="Este reporte incluye todos los hijos menores de 18 años y los hijos con estudios superiores menores de 24 años."
                    type="info"
                    style={{ marginBottom: 16 }}
                />
                
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={8}>
                        <Statistic 
                            title="Total Hijos" 
                            value={datosReporte.length} 
                        />
                    </Col>
                    <Col span={8}>
                        <Statistic 
                            title="Aplican Asig. Familiar" 
                            value={datosReporte.filter(d => d.aplicaAsignacionFamiliar).length}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Col>
                    <Col span={8}>
                        <Statistic 
                            title="No Aplican" 
                            value={datosReporte.filter(d => !d.aplicaAsignacionFamiliar).length}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Col>
                </Row>
                
                <Table
                    columns={columnsReporte}
                    dataSource={datosReporte}
                    rowKey={(record) => `${record.colaboradorId}-${record.hijoDocumento}`}
                    pagination={{ pageSize: 10 }}
                    size="small"
                    scroll={{ y: 400 }}
                />
            </Modal>

            {/* Modal para agregar hijo */}
            <Modal
                title={<Space><PlusOutlined /><span>Agregar Hijo</span></Space>}
                visible={modalAgregarVisible}
                onCancel={() => setModalAgregarVisible(false)}
                footer={null}
                width={560}
                destroyOnClose
            >
                <Form
                    form={formAgregar}
                    layout="vertical"
                    onFinish={handleAgregarHijo}
                >
                    <Form.Item
                        name="colaboradorId"
                        label="Colaborador"
                        rules={[{ required: true, message: 'Seleccione el colaborador' }]}
                    >
                        <Select
                            showSearch
                            placeholder="Buscar por nombre o DNI"
                            filterOption={false}
                            onSearch={onBuscarColaboradores}
                            notFoundContent="Sin resultados"
                            allowClear
                        >
                            {colaboradoresBusqueda.map((c) => (
                                <Option key={c.id} value={c.id}>
                                    {c.nombreCompleto} — {c.numeroDocumentoIdentidad}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="tipo"
                        label="Tipo de Hijo"
                        rules={[{ required: true, message: 'Seleccione el tipo' }]}
                    >
                        <Select
                            placeholder="Seleccionar tipo"
                            onChange={setTipoHijoSeleccionado}
                        >
                            <Option value="MENOR_EDAD">
                                <Tag color="cyan" icon={<UserOutlined />}>Menor de Edad</Tag>
                            </Option>
                            <Option value="ESTUDIO_SUPERIOR">
                                <Tag color="purple" icon={<BookOutlined />}>Estudio Superior</Tag>
                            </Option>
                        </Select>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="nombreCompleto"
                                label="Nombre Completo"
                                rules={[{ required: true, message: 'Ingrese el nombre' }]}
                            >
                                <Input placeholder="Apellidos y nombres" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="numeroDocumentoIdentidad"
                                label="Nº Documento"
                                rules={[{ required: true, message: 'Ingrese el documento' }]}
                            >
                                <Input placeholder="DNI / CE" maxLength={20} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="fechaNacimiento"
                        label="Fecha de Nacimiento"
                        rules={[{ required: true, message: 'Seleccione la fecha de nacimiento' }]}
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            format="DD/MM/YYYY"
                            disabledDate={(current) => current && current > moment().endOf('day')}
                            placeholder="DD/MM/YYYY"
                        />
                    </Form.Item>

                    {tipoHijoSeleccionado === 'ESTUDIO_SUPERIOR' && (
                        <>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="centroEstudios"
                                        label="Centro de Estudios"
                                        rules={[{ required: true, message: 'Ingrese el centro de estudios' }]}
                                    >
                                        <Input placeholder="Universidad / Instituto" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="carrera"
                                        label="Carrera"
                                        rules={[{ required: true, message: 'Ingrese la carrera' }]}
                                    >
                                        <Input placeholder="Ej: Ingeniería de Sistemas" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item name="cicloSemestre" label="Ciclo / Semestre">
                                <Input placeholder="Ej: 5to ciclo" />
                            </Form.Item>
                        </>
                    )}

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setModalAgregarVisible(false)}>Cancelar</Button>
                            <Button type="primary" htmlType="submit" loading={loadingAgregar} icon={<PlusOutlined />}>
                                Agregar Hijo
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            <style jsx>{`
                .row-critico {
                    background-color: #fff1f0;
                }
                .row-advertencia {
                    background-color: #fffbe6;
                }
            `}</style>
        </div>
    );
};

export default BandejaDerechoHabienteHijos;